import * as moment from 'moment';
import { Moment } from 'moment';
import { environment } from '../../../environments/environment';
import { Country } from '../models/common/country';
import { MOMENT_UNIT } from './moment';
import { capitalize, isString } from './string';
import { ListSortDirection } from '@core/models/common/list-sort-direction';
import { FORMAT } from './formats';
import { TimeListItem } from '@core/models/common/date-time/time-list-item';
import { DatetimeBase } from '@core/models/common/date-time/datetime-base';
import { DatetimeUnit } from '@components/datetime/datetime-unit';
import { TextInputBase } from '@core/models/common/date-time/text-input-base';
import { isNullOrUndefined } from './object';

export const hoursList: string[] = [
    '00',
    '01',
    '02',
    '03',
    '04',
    '05',
    '06',
    '07',
    '08',
    '09',
    '10',
    '11',
    '12',
    '13',
    '14',
    '15',
    '16',
    '17',
    '18',
    '19',
    '20',
    '21',
    '22',
    '23',
    '24',
];

export const minutesList: string[] = generateArrayFromNumber(60);

export const secondsList: string[] = generateArrayFromNumber(60);

export function generateArrayFromNumber(value: number): string[] {
    const result: string[] = [];

    for (let i = 0; i < value; i++) {
        result.push(pad(i));
    }

    return result;
}

export function pad(value: number): string {
    if (value > 9) {
        return '' + value;
    } else {
        return '0' + value;
    }
}

/**
 * Checks if the given value is a valid js string-pipes
 *
 * @export
 * @param {*} value
 * @returns {boolean}
 */
export function minHumanize(
    value: number,
    removeLeadingZero?: boolean
): string {
    let hours = Math.floor(value / 60);
    let minutes = value % 60;

    if (removeLeadingZero) {
        return hours + '.' + padNumber(minutes);
    } else {
        return padNumber(hours) + '.' + padNumber(minutes);
    }
}

export function minutesToHours(value: number): number {
    return value / 60;
}

export function hoursToMinutes(value: number): number {
    return value * 60;
}

export function padNumber(value: number): string {
    let result = '';
    result = (value < 10 ? '0' : '') + value;
    return result;
}

/**
 * Returns a formatted UTC date
 *
 * @export
 * @param {string} [format]
 * @returns {string}
 */
export function getUTCDate(format?: string): string {
    const dateFormat = format || FORMAT.DATETIME_LONG;
    return moment().utc().format(dateFormat);
}

export function restrictToAllowedTimeFormatInput(value: string): string {
    const regex = /^[1-9]([ap]{1}|[:.][0-9]{0,2}[ap]{0,1}|[0-9:.]{0,2}[ap]{0,1}|[0-9]{3}[ap]{0,1}|[0-9][.:][0-9]{1,2}[ap]{0,1})$/i;

    function getString(input) {
        if (!regex.test(input)) {
            if (input.length <= 1) {
                return '';
            } else {
                let temp = input.substring(0, input.length - 1);
                return getString(temp);
            }
        } else {
            return input;
        }
    }

    let result = getString(value);
    return result.trim();
}

export function filterTimeList(
    searchInput: string,
    timesList: any[],
    interval?: number
): any[] {
    let list: any[] = timesList;
    let filtered = [];
    let timeInterval = interval;

    function addItem(index) {
        list[index].index = index;
        filtered.push(list[index]);
    }

    function replaceMinutes(str) {
        if (timeInterval === 1) {
            return str;
        } else if (timeInterval === 5) {
            if (['0', '58', '59', '01', '02'].indexOf(str) !== -1) {
                return '00';
            } else if (['03', '04', '05', '06', '07'].indexOf(str) !== -1) {
                return '05';
            } else if (
                ['1', '08', '09', '10', '11', '12'].indexOf(str) !== -1
            ) {
                return '10';
            } else if (['13', '14', '16', '17'].indexOf(str) !== -1) {
                return '15';
            } else if (
                ['2', '18', '19', '20', '21', '22'].indexOf(str) !== -1
            ) {
                return '20';
            } else if (['23', '24', '25', '26', '27'].indexOf(str) !== -1) {
                return '25';
            } else if (['3', '28', '29', '31', '32'].indexOf(str) !== -1) {
                return '30';
            } else if (['33', '34', '35', '36', '37'].indexOf(str) !== -1) {
                return '35';
            } else if (
                ['4', '38', '39', '40', '41', '42'].indexOf(str) !== -1
            ) {
                return '40';
            } else if (['43', '44', '45', '46', '47'].indexOf(str) !== -1) {
                return '45';
            } else if (
                ['5', '48', '49', '50', '51', '52'].indexOf(str) !== -1
            ) {
                return '50';
            } else if (['53', '54', '55', '56', '57'].indexOf(str) !== -1) {
                return '55';
            } else {
                return str;
            }
        } else {
            if (
                [
                    '1',
                    '2',
                    '08',
                    '09',
                    '10',
                    '11',
                    '12',
                    '13',
                    '14',
                    '16',
                    '17',
                    '18',
                    '19',
                    '20',
                    '21',
                    '22',
                ].indexOf(str) !== -1
            ) {
                return '15';
            } else if (
                [
                    '0',
                    '53',
                    '54',
                    '55',
                    '56',
                    '57',
                    '58',
                    '59',
                    '01',
                    '02',
                    '03',
                    '04',
                    '05',
                    '06',
                    '07',
                ].indexOf(str) !== -1
            ) {
                return '00';
            } else if (
                [
                    '3',
                    '23',
                    '24',
                    '25',
                    '26',
                    '27',
                    '28',
                    '29',
                    '31',
                    '32',
                    '33',
                    '34',
                    '35',
                    '36',
                    '37',
                ].indexOf(str) !== -1
            ) {
                return '30';
            } else if (
                [
                    '4',
                    '5',
                    '38',
                    '39',
                    '40',
                    '41',
                    '42',
                    '43',
                    '44',
                    '46',
                    '47',
                    '48',
                    '49',
                    '50',
                    '51',
                    '52',
                ].indexOf(str) !== -1
            ) {
                return '45';
            } else {
                return str;
            }
        }
    }

    function isNextHour(hours, minutes) {
        if (timeInterval === 5) {
            if (['58', '59'].indexOf(minutes) > -1) {
                return 1 + parseInt(hours, 10);
            }
        } else if (timeInterval === 15) {
            if (
                ['53', '54', '55', '56', '57', '58', '59'].indexOf(minutes) > -1
            ) {
                return 1 + parseInt(hours, 10);
            }
        }

        return parseInt(hours, 10);
    }

    filtered = [];

    if (searchInput !== '') {
        const regex1 = /^[1-9]{1}[0-9]{3}[ap]{0,1}$/i; // Format: 1050(a/p) OK
        const regex2 = /^[1-9]{1}[:.][0-9]{1}[ap]{0,1}$/i; // Format: 1.3 (a/p) OK
        const regex3 = /^[1-9]{1}[:.][0-9]{2}[ap]{0,1}$/i; // Format: 1.15a / 1:20a OK
        const regex4 = /^[1-9]{1}[0-9]{2}[ap]{0,1}$/i; // Format 112(a/p) OK
        const regex5 = /^[1-9]{1}[0-9]{1}[.:]{1}[ap]{0,1}$/i; // Format 11.(a/p) OK
        const regex6 = /^[1-9]{1}[:.]{1}[ap]{0,1}$/i; // Format 1.(a/p) OK
        const regex7 = /^[1-9]{1}[0-9][.:][0-9]{1}[ap]{0,1}$/i; // Format 11.2(a/p) OK
        const regex8 = /^[1-9]{1}[0-9][.:][0-9]{2}[ap]{0,1}$/i; // Format 11.20(a/p) OK
        const regex9 = /^[1-9][0-9][ap]{0,1}$/i; // Format: 11(a/p)
        const regex10 = /^[1-9]{1}[ap]{0,1}$/i; // Format: 8(a/p)

        for (let i = 0, len = list.length; i < len; i++) {
            if (searchInput && searchInput.length > 0) {
                let input = searchInput.replace(/[^\w\s]/gi, '').toLowerCase();
                let withoutSeparatorWithT = list[i].time
                    .replace(/\:/g, '')
                    .toLowerCase();
                let inputWOSeparatorWithT = input
                    .replace(/\:/g, '')
                    .replace(/\./g, '')
                    .toLowerCase();

                input = searchInput;

                if (regex1.test(input)) {
                    // 2350/1230(a|p)/1115/1018
                    if (withoutSeparatorWithT.indexOf(input) !== -1) {
                        addItem(i);
                    } else {
                        let minutes = input.substr(2, 2);
                        let hours = input.substr(0, 2);
                        let processedHours = isNextHour(hours, minutes);
                        let pre = '';

                        if (processedHours === 24) {
                            pre = 12 + replaceMinutes(minutes) + 'a';
                        } else if (processedHours > 12 && processedHours < 24) {
                            let temp = processedHours % 12;
                            pre =
                                (temp < 3 ? '0' + temp : temp) +
                                replaceMinutes(minutes) +
                                'p';
                        } else {
                            pre =
                                processedHours +
                                replaceMinutes(minutes) +
                                input.substr(4);
                        }

                        if (withoutSeparatorWithT.indexOf(pre) !== -1) {
                            addItem(i);
                        }
                    }
                } else if (regex2.test(input)) {
                    // Format: 1.3 (a/p)
                    inputWOSeparatorWithT = '0' + inputWOSeparatorWithT;

                    if (
                        withoutSeparatorWithT.indexOf(inputWOSeparatorWithT) !==
                        -1
                    ) {
                        addItem(i);
                    } else {
                        let pre =
                            inputWOSeparatorWithT.substr(0, 2) +
                            replaceMinutes(inputWOSeparatorWithT.substr(2, 1)) +
                            inputWOSeparatorWithT.substr(3);
                        if (withoutSeparatorWithT.indexOf(pre) !== -1) {
                            addItem(i);
                        }
                    }
                } else if (regex3.test(input)) {
                    // Format 1.15a / 1:20a
                    inputWOSeparatorWithT = '0' + inputWOSeparatorWithT;
                    if (
                        withoutSeparatorWithT.indexOf(inputWOSeparatorWithT) !==
                        -1
                    ) {
                        addItem(i);
                    } else {
                        let minutes = inputWOSeparatorWithT.substr(2, 2);
                        let hours = inputWOSeparatorWithT.substr(0, 2);
                        let processedHours = isNextHour(hours, minutes);

                        let pre =
                            '0' +
                            processedHours +
                            replaceMinutes(minutes) +
                            inputWOSeparatorWithT.substr(4);

                        if (withoutSeparatorWithT.indexOf(pre) !== -1) {
                            addItem(i);
                        }
                    }
                } else if (regex4.test(input)) {
                    // Format 112(a/p)

                    input = '0' + input;
                    let minutes = input.substr(2, 2);
                    let hours = input.substr(0, 2);
                    let processedHours = isNextHour(hours, minutes);

                    if (
                        withoutSeparatorWithT.indexOf(
                            '0' +
                                processedHours +
                                replaceMinutes(minutes) +
                                input.substr(4)
                        ) !== -1
                    ) {
                        addItem(i);
                    }
                } else if (regex5.test(input)) {
                    // Format 11.(a/p)

                    let value = parseInt(
                        inputWOSeparatorWithT.substr(0, 2),
                        10
                    );
                    let pre = '';

                    if (value > 12 && value < 25) {
                        let temp = value % 12;
                        pre = (temp < 3 ? '0' + temp : temp) + '00p';
                    } else {
                        pre =
                            inputWOSeparatorWithT.substr(0, 2) +
                            '00' +
                            inputWOSeparatorWithT.substr(2);
                    }

                    if (withoutSeparatorWithT.indexOf(pre) !== -1) {
                        addItem(i);
                    }
                } else if (regex6.test(input)) {
                    // 1.(a/p)
                    let pre =
                        '0' +
                        inputWOSeparatorWithT.substr(0, 1) +
                        '00' +
                        inputWOSeparatorWithT.substr(1);
                    if (withoutSeparatorWithT.indexOf(pre) !== -1) {
                        addItem(i);
                    }
                } else if (regex7.test(input)) {
                    // Format 11.2(a/p)
                    if (
                        withoutSeparatorWithT.indexOf(inputWOSeparatorWithT) !==
                        -1
                    ) {
                        addItem(i);
                    } else {
                        let value = parseInt(
                            inputWOSeparatorWithT.substr(0, 2),
                            10
                        );
                        let pre = '';

                        if (value > 12 && value < 25) {
                            let temp = value % 12;
                            pre =
                                (temp < 3 ? '0' + temp : temp) +
                                replaceMinutes(
                                    inputWOSeparatorWithT.substr(2, 1)
                                ) +
                                'p';
                        } else {
                            pre =
                                inputWOSeparatorWithT.substr(0, 2) +
                                replaceMinutes(
                                    inputWOSeparatorWithT.substr(2, 1)
                                ) +
                                inputWOSeparatorWithT.substr(3);
                        }

                        if (withoutSeparatorWithT.indexOf(pre) !== -1) {
                            addItem(i);
                        }
                    }
                } else if (regex8.test(input)) {
                    // Format 11.20(a/p)
                    if (
                        withoutSeparatorWithT.indexOf(inputWOSeparatorWithT) !==
                        -1
                    ) {
                        addItem(i);
                    } else {
                        let minutes = inputWOSeparatorWithT.substr(2, 2);
                        let hours = inputWOSeparatorWithT.substr(0, 2);
                        let processedHours = isNextHour(hours, minutes);

                        let value = parseInt(
                            inputWOSeparatorWithT.substr(0, 2),
                            10
                        );
                        let pre = '';

                        if (processedHours === 24) {
                            pre = 12 + replaceMinutes(minutes) + 'a';
                        } else if (processedHours > 12 && processedHours < 23) {
                            let temp = processedHours % 12;
                            pre =
                                (temp < 3 ? '0' + temp : temp) +
                                replaceMinutes(minutes) +
                                'p';
                        } else {
                            pre =
                                processedHours +
                                replaceMinutes(minutes) +
                                inputWOSeparatorWithT.substr(4);
                        }

                        if (withoutSeparatorWithT.indexOf(pre) !== -1) {
                            addItem(i);
                        }
                    }
                } else if (regex9.test(input)) {
                    // Format: 11(a/p)

                    let value = parseInt(
                        inputWOSeparatorWithT.substr(0, 2),
                        10
                    );
                    let pre = '';

                    if (value > 12 && value < 25) {
                        let temp = value % 12;
                        pre = (temp < 3 ? '0' + temp : temp) + '00p';
                    } else {
                        pre =
                            inputWOSeparatorWithT.substr(0, 2) +
                            '00' +
                            inputWOSeparatorWithT.substr(2);
                    }

                    if (withoutSeparatorWithT.indexOf(pre) !== -1) {
                        addItem(i);
                    }
                } else if (regex10.test(input)) {
                    // Format: 8(a/p)
                    inputWOSeparatorWithT = input
                        .replace(/\:/g, '')
                        .replace(/\./g, '')
                        .toLowerCase();
                    let pre =
                        '0' +
                        inputWOSeparatorWithT.substr(0, 1) +
                        '00' +
                        inputWOSeparatorWithT.substr(1);
                    if (withoutSeparatorWithT.indexOf(pre) !== -1) {
                        addItem(i);
                    }
                }
            }
        }
    } else {
        this.filtered = list.map((currentValue, index) => {
            currentValue.index = index;
            return currentValue;
        });
    }

    return filtered;
}

export function getWeeklyDaysArray(startDayOfWeek: number): number[] {
    let daysOfWeek: number[] = [];
    for (let i = 0; i < 7; i++) {
        daysOfWeek.push((startDayOfWeek + i) % 7);
    }
    return daysOfWeek;
}

export function getWeekForCurrentDate(
    startOfWeek: number,
    startTimeOfDay: string = '00:00:00'
): { start: Moment; end: Moment } {
    const [hour, minute, second] = startTimeOfDay.split(':');
    const today: Moment = moment()
        .hour(+hour)
        .minute(+minute)
        .second(+second);

    const dayOfToday: number = parseInt(today.format('d'), 10);
    const daysArr: number[] = getWeeklyDaysArray(startOfWeek);
    const indexDayOfToday: number = daysArr.indexOf(dayOfToday);

    return {
        start: moment(today).subtract(indexDayOfToday, MOMENT_UNIT.DAYS),
        end: moment(today).add(6 - indexDayOfToday, MOMENT_UNIT.DAYS),
    };
}

export function getWeekForDate(startOfWeek, date) {
    let today = moment(date);
    let dayOfToday = parseInt(today.format('d'), 10);
    let daysArr = getWeeklyDaysArray(startOfWeek);
    let indexDayOfToday = daysArr.indexOf(dayOfToday);

    return {
        start: moment(today).subtract(indexDayOfToday, MOMENT_UNIT.DAYS),
        end: moment(today).add(6 - indexDayOfToday, MOMENT_UNIT.DAYS),
    };
}

export function serverDateTimeToLocalDateObj(dateStr): Date {
    return moment.utc(dateStr, FORMAT.DATETIME_LONG, true).toDate();
}

export function getLocalMomentFromUtc(
    dateStr: string,
    format: string = FORMAT.DATETIME_LONG
): any {
    return dateStr ? moment.utc(dateStr, format).local() : dateStr;
}

/**
 * Returns the list of times from the start of the day at 12:00 AM with the given step count
 *
 * @param {number} step interval between each time item
 * @returns {TimeListItem[]}
 */
// export function getTimeListForToday(step: number): TimeListItem[] {
//     const startMoment: Moment = moment().startOf(MOMENT_UNIT.DAYS);
//     // 24 * 60 - minutes in a day (24H)
//     const slotCount: number = (24 * 60) / step;
//     return getTimeList(startMoment, slotCount, step);
// }

// export function getTimeListForDay(
//     dayMoment: Moment,
//     step: number = 5
// ): TimeListItem[] {
//     const startMoment: Moment = dayMoment.clone().startOf(MOMENT_UNIT.DAYS);
//     // 24 * 60 - minutes in a day (24H)
//     const slotCount: number = (24 * 60) / step;
//     return getTimeList(startMoment, slotCount, step);
// }

// export function getTimeList(
//     startTime: string | Date | Moment,
//     slotCount: number,
//     durationPerSlot?: number
// ): TimeListItem[] {
//     let res: TimeListItem[] = [];
//     const startMoment: Moment = moment.isMoment(startTime)
//         ? startTime
//         : getMomentFromDate(startTime);

//     let step: number = durationPerSlot;

//     if (!step) {
//         // 24 * 60 - minutes in a day (24H)
//         step = (24 * 60) / environment.settings.grid.numberOfSlotsPerDay;
//     }

//     for (let i = 0; i < slotCount; i++) {
//         const availMoment: Moment = startMoment.clone().startOf('day');
//         availMoment.add(i * step, 'minutes');
//         res.push(
//             new TimeListItem(
//                 availMoment,
//                 availMoment.format(FORMAT.TIME_DISPLAY),
//                 availMoment.toDate(),
//                 undefined,
//                 i
//             )
//         );
//     }

//     return res;
// }

export function getMomentFromDate(targetDate) {
    let targetMoment;

    // Convert a string-pipes to moment
    if (isString(targetDate)) {
        let convertFormat = FORMAT.DATETIME_LONG;
        if (targetDate.length === 10) {
            convertFormat = FORMAT.DATE_SHORT;
        } else if (targetDate.length === 16) {
            convertFormat = FORMAT.DATETIME_SHORT;
        }

        targetMoment = moment(targetDate, convertFormat);
    } else {
        // Convert a date or moment to moment
        targetMoment = moment(targetDate);
    }

    return targetMoment;
}

export function fromUTC(date: string): Date {
    return moment.utc(getMomentFromDate(date)).toDate();
}

export function fromNow(dateVal: Date, isUTC?: boolean): string {
    let res = '';
    let dateMoment = getMomentFromDate(dateVal);
    if (dateMoment) {
        if (isUTC) {
            dateMoment.utc();
        }
        res = dateMoment.fromNow();
    }

    return res;
}

/**
 * Returns the day position in terms of 1st Week, 2nd Week... according to the calendar month
 *
 * @export
 * @param {Moment} date
 * @returns
 */
export function getWeekIndexOfMonth(date: Moment) {
    let firstDay = date.clone().startOf('month').day();
    return Math.ceil((date.date() + firstDay) / 7);
}

export function getDayOfWeekIndex(date: Moment) {
    return date.day();
}

export function getDurationForExpiryDate(
    expiry: string,
    unitOfTime: MOMENT_UNIT = MOMENT_UNIT.DAYS,
    precise: boolean = false,
    addOne: boolean = true
): number {
    let expireMoment: Moment;
    if (!expiry || !moment(expiry, FORMAT.DATE_SHORT).isValid()) {
        return;
    }
    // Consider the time from the start of today
    const currentDay = moment().startOf('day');

    // Adding 1 day to the expiry day as things* actually expire at 12 am the next day
    if (addOne) {
        expireMoment = moment(expiry, FORMAT.DATE_SHORT).add(
            1,
            MOMENT_UNIT.DAYS
        );
    } else {
        expireMoment = moment(expiry, FORMAT.DATE_SHORT);
    }

    let daysDuration: number;

    switch (unitOfTime) {
        case MOMENT_UNIT.YEARS: {
            daysDuration = expireMoment.diff(currentDay, unitOfTime, precise);
            break;
        }
        case MOMENT_UNIT.MONTHS: {
            daysDuration = expireMoment.diff(currentDay, unitOfTime, precise);
            break;
        }
        case MOMENT_UNIT.DAYS:
        default:
            daysDuration = expireMoment.diff(
                currentDay,
                MOMENT_UNIT.DAYS,
                precise
            );
            break;
    }

    return daysDuration;
}

/**
 * Returns the week day based on the oss week number since oss week number runs from 1-7 and moment counts from 0-6.
 * So oss sunday 7 = moment sunday 0
 *
 * @param ossWeekNumber
 * @param short
 */
export function getOSSWeekDay(
    ossWeekNumber: number,
    short: boolean = false
): string {
    if (ossWeekNumber < 0 && ossWeekNumber > 7) {
        return;
    }

    const momentWeekNumber: number = ossWeekNumber === 7 ? 0 : ossWeekNumber;
    return !short
        ? moment.weekdays(momentWeekNumber)
        : moment.weekdaysShort(momentWeekNumber);
}

/**
 * Returns the list of days in a week
 * @returns {string[]}
 */
export function getWeekDays(): string[] {
    // cos moment treats weekdays from `Sunday` to `Saturday` as 0-6
    // and backend treats weekdays from `Monday` to `Sunday` as 1-7
    return [
        moment.weekdays(1),
        moment.weekdays(2),
        moment.weekdays(3),
        moment.weekdays(4),
        moment.weekdays(5),
        moment.weekdays(6),
        moment.weekdays(0),
    ];
}

/**
 * Returns the list of days in a week as an array of objects needed by the dropdown component
 *
 * @returns {{id: number; label: string; value: number}[]}
 */
export function getWeekDaysFormatted(): {
    id: number;
    label: string;
    value: number;
}[] {
    const weekDays: string[] = getWeekDays();
    return weekDays.map((day: string, index: number) => {
        return {
            id: index + 1,
            label: day,
            value: index + 1,
        };
    });
}

export function sortByObjectDateTime(
    prop?: string,
    sortDirection: ListSortDirection = ListSortDirection.DESCENDING
) {
    return (objectA: any, objectB: any) => {
        const objectAMoment: Moment = moment(
            !prop ? objectA : objectA[prop],
            FORMAT.DATETIME_SERVER
        );
        const objectBMoment: Moment = moment(
            !prop ? objectB : objectB[prop],
            FORMAT.DATETIME_SERVER
        );

        if (objectAMoment.isAfter(objectBMoment)) {
            return sortDirection === ListSortDirection.DESCENDING ? -1 : 1;
        } else if (objectAMoment.isBefore(objectBMoment)) {
            return sortDirection === ListSortDirection.DESCENDING ? 1 : -1;
        }

        return 0;
    };
}

/**
 * Returns an array of objects (dropdown item format) to populate hours/minutes fields
 * @param minCount start value
 * @param maxCount end value inclusive
 * @param suffix any suffix needed for the label like 'm' 'h'
 * @param step increment each value by how many steps. Defaults to 1
 */
export function getTimeOptions(
    minCount: number,
    maxCount: number,
    suffix: string = '',
    step: number = 1
): {
    id: number;
    value: number;
    label: string;
}[] {
    const res: {
        id: number;
        value: number;
        label: string;
    }[] = [];

    const diff: number = maxCount - minCount;
    for (let i = 0, m = diff; i <= m; i = i + step) {
        res.push({ id: i, value: minCount + i, label: minCount + i + suffix });
    }

    return res;
}

export function getDatetimeFormatFromCountry(country: Country) {
    let datetimeFormat = null;
    if (country) {
        switch (country.country_code) {
            case 'US':
            case 'CA':
                datetimeFormat = 2;
                break;
            default:
                datetimeFormat = 0;
                break;
        }
    } else {
        datetimeFormat = 0;
    }
    return datetimeFormat;
}

/**
 * Returns the list of times from the start of the day at 12:00 AM with the given step count
 *
 * @param {number} step interval between each time item
 * @returns {TimeListItem[]}
 */
export function getTimeListForToday(step: number): TimeListItem[] {
    const startMoment: Moment = moment().startOf(MOMENT_UNIT.DAYS);
    // 24 * 60 - minutes in a day (24H)
    const slotCount: number = (24 * 60) / step;
    return getTimeList(startMoment, slotCount, step);
}

export function getTimeListForDay(
    dayMoment: Moment,
    step: number = 5
): TimeListItem[] {
    const startMoment: Moment = dayMoment.clone().startOf(MOMENT_UNIT.DAYS);
    // 24 * 60 - minutes in a day (24H)
    const slotCount: number = (24 * 60) / step;
    return getTimeList(startMoment, slotCount, step);
}

export function getTimeList(
    startTime: string | Date | Moment,
    slotCount: number,
    durationPerSlot?: number
): TimeListItem[] {
    let res: TimeListItem[] = [];
    const startMoment: Moment = moment.isMoment(startTime)
        ? startTime
        : getMomentFromDate(startTime);

    let step: number = durationPerSlot;

    if (!step) {
        // 24 * 60 - minutes in a day (24H)
        step = (24 * 60) / environment.settings.grid.numberOfSlotsPerDay;
    }

    for (let i = 0; i < slotCount; i++) {
        const availMoment: Moment = startMoment.clone().startOf('day');
        availMoment.add(i * step, 'minutes');
        res.push(
            new TimeListItem(
                availMoment,
                availMoment.format(FORMAT.TIME_DISPLAY),
                availMoment.toDate(),
                undefined,
                i
            )
        );
    }

    return res;
}

export function buildDatetimeFormat(
    datetime: string,
    format: string,
    id: number,
    sessionLocale: string,
    locales: any
): DatetimeBase<any>[] {
    let datetimeFormatted: DatetimeBase<any>[] = [];

    if (moment(datetime).isValid()) {
        datetimeFormatted = mapDatetimeFormat(
            datetime,
            format,
            id,
            sessionLocale,
            locales
        );
    }

    return datetimeFormatted;
}

function mapDatetimeFormat(
    datetime: string,
    format: string,
    id: number,
    sessionLocale: string,
    locales: any
) {
    const dt: string = moment(datetime).format(format);
    const mapValue: DatetimeBase<any>[] = [];
    const dateString: any[] = dt.toString().split(/[ -/]+/);

    let dateSeparator: string = checkDateSeparator(dt);
    let orderTracker: number = 0;
    let hasCalendar: boolean = false;
    let separatorCounter: number = 0;

    dateString.forEach((item, key, array) => {
        const timeExpr: any = /[:]+/;

        let maxLength = item.length;
        let mapFormat: any = {};
        let cssClass: string = '';

        // slit the time
        if (timeExpr.test(item)) {
            splitTimeString(
                dateString,
                datetime,
                mapValue,
                orderTracker,
                id,
                hasCalendar
            );
            return;
        }

        if (Object.is(array.length - 1, key)) {
            // for last item, no separator needed
            dateSeparator = ' ';
        }

        if (item === 'AM' || item === 'am' || item === 'PM' || item === 'pm') {
            return;
        }

        // ignore GMT+1000
        if (item === 'GMT+1000') {
            return;
        }

        if (maxLength === 3 || maxLength === 4) {
            cssClass = 'c-dynamic-form__input c-dynamic-form__input--md';
        } else if (maxLength > 4) {
            cssClass = 'c-dynamic-form__input c-dynamic-form__input--lg';
        }

        hasCalendar = true;
        mapFormat = dateFormatString(format, key, sessionLocale, locales);
        orderTracker = key;

        if (dateSeparator === '/' || dateSeparator === '-') {
            separatorCounter = separatorCounter + 1;

            if (separatorCounter > 2) {
                dateSeparator = ' ';
            }
        }

        let inputType: string = 'text';
        mapValue.push(
            new TextInputBase({
                key: `${mapFormat.key}-${id}`,
                value:
                    mapFormat.key === 'dddd' || mapFormat.key === 'ddd'
                        ? item + ','
                        : item,
                required: true,
                order: orderTracker,
                type: inputType,
                cssClass: maxLength <= 2 ? 'c-dynamic-form__input' : cssClass,
                maxLength: mapFormat.maxLength,
                datetimeFormat: mapFormat.format,
                datetimeUnit: mapFormat.unit,
                separator:
                    dateSeparator === '-' ||
                    dateSeparator === '/' ||
                    dateSeparator === ' '
                        ? dateSeparator
                        : ':',
                datetime,
                hasCalendar: true,
            })
        );
    });

    return mapValue;
}

function checkDateSeparator(date: string) {
    const dashSeparator: any = /-/;
    const slashSeparator: any = /\//;
    const spaceSeparator: any = / /;

    if (dashSeparator.test(date)) {
        return '-';
    } else if (slashSeparator.test(date)) {
        return '/';
    } else if (spaceSeparator.test(date)) {
        return ' ';
    }
}

function splitTimeString(
    timeString: any[],
    datetime: string,
    map: DatetimeBase<any>[],
    orderTracker: number,
    id: number,
    hasCalendar: boolean
) {
    timeString.forEach((item, key, array) => {
        const timeExpr: any = /[:]+/;
        let timeSeparator: any = ':';
        let mapFormat: { key; unit; format } = null;

        orderTracker = orderTracker + key;

        if (timeExpr.test(item)) {
            let timeItem = item.split(':');

            timeItem.forEach((itemTime, keyTime, arrayTIme) => {
                mapFormat = timeFormatString(keyTime);
                orderTracker = orderTracker + keyTime;

                if (Object.is(arrayTIme.length - 1, keyTime)) {
                    // for last item, no separator needed
                    timeSeparator = '';

                    // ignore GMT+1000
                    if (item === 'GMT+1000') {
                        return;
                    }
                }

                map.push(
                    new TextInputBase({
                        key: `${mapFormat.unit}-${id}`,
                        value: itemTime,
                        type: 'number',
                        required: true,
                        order: orderTracker,
                        cssClass: 'c-dynamic-form__input',
                        maxLength: 2,
                        datetimeUnit: mapFormat.unit,
                        datetimeFormat: mapFormat.format,
                        separator: timeSeparator,
                        datetime,
                        hasCalendar,
                    })
                );
            });
        }

        if (Object.is(array.length - 1, key)) {
            // for last item, no separator needed
            timeSeparator = '';

            // ignore GMT+1000
            if (item === 'GMT+1000') {
                return;
            }
        }

        if (item === 'AM' || item === 'am' || item === 'PM' || item === 'pm') {
            let format: string = 'a';
            if (item === 'AM' || item === 'PM') {
                format = 'A';
            }

            orderTracker = orderTracker + key;
            map.push(
                new TextInputBase({
                    key: `AMPM-${id}`,
                    value: item,
                    required: true,
                    type: 'text',
                    order: orderTracker,
                    cssClass: 'c-dynamic-form__input',
                    maxLength: 2,
                    datetimeUnit: DatetimeUnit.AMPM,
                    datetimeFormat: format,
                    separator: timeSeparator,
                    datetime,
                    hasCalendar,
                })
            );
        }
    });
}

export function onChangeDatetime(
    dateTime: Moment,
    value: string,
    unit: DatetimeUnit,
    format: any,
    sessionLocale?: string,
    locales?: any
) {
    let newValue: string;
    let period: any = parseInt(value, 10);

    // Convert month name in number
    if (isNaN(period)) {
        switch (sessionLocale) {
            case locales[0].value:
                period = moment.monthsShort().indexOf(capitalize(value)) + 1;
                break;
            case locales[1].value:
                period = moment.monthsShort().indexOf(value) + 1;
                break;
            default:
                period = moment.monthsShort().indexOf(capitalize(value)) + 1;
        }
        if (period === 0) {
            return dateTime;
        }
    }

    if (!value) {
        return dateTime;
    }
    // Here we need to replace unit === 'day' to 'date'
    if (unit === DatetimeUnit.DAY) {
        newValue = moment(dateTime).set('date', period).format(format);
    } else if (unit === DatetimeUnit.MONTH) {
        // momentjs bug: it always increments 1 for value that you type for the month field.
        newValue = moment(dateTime)
            .set(unit, period - 1)
            .format(format);
    } else if (unit === DatetimeUnit.AMPM) {
        newValue = moment(dateTime).set('hour', period).format(format);
    } else {
        newValue = moment(dateTime).set(unit, period).format(format);
    }

    return newValue;
}

function timeFormatString(key: number) {
    if (key === 0) {
        return {
            key,
            unit: DatetimeUnit.HOUR,
            format: 'hh',
        };
    } else if (key === 1) {
        return {
            key,
            unit: DatetimeUnit.MINUTE,
            format: 'mm',
        };
    } else if (key === 2) {
        return {
            key,
            unit: DatetimeUnit.SECOND,
            format: 'mm',
        };
    } else {
        return {
            key,
            unit: DatetimeUnit.HOUR,
            format: 'hh',
        };
    }
}

function dateFormatString(
    format: string,
    key: number,
    sessionLocale: string,
    locales: any
) {
    let formatted: any = '';
    let maxLength: number = null;

    if (isNullOrUndefined(format) || !format) {
        format = FORMAT.DATE_INPUT;
    }

    formatted = format.split(/[ -/]+/);

    if (formatted[key] === 'dddd' || formatted[key] === 'ddd') {
        maxLength = null;
        return {
            key: 'dddd',
            unit: DatetimeUnit.DAY,
            format: formatted[key],
            maxLength,
        };
    } else if (formatted[key] === 'D' || formatted[key] === 'DD') {
        maxLength = 2;
        return {
            key: DatetimeUnit.DAY,
            unit: DatetimeUnit.DAY,
            format: formatted[key],
            maxLength,
        };
    } else if (formatted[key] === 'MM') {
        maxLength = 2;
        return {
            key: DatetimeUnit.MONTH,
            unit: DatetimeUnit.MONTH,
            format: formatted[key],
            maxLength,
        };
    } else if (formatted[key] === 'MMM' || formatted[key] === 'MMMM') {
        // locale for month will change here
        if (locales && locales.length) {
            switch (sessionLocale) {
                case locales[0].value:
                    maxLength = 3;
                    break;
                case locales[1].value:
                    maxLength = 4;
                    break;
                default:
                    maxLength = 3;
            }
        } else {
            maxLength = 3;
        }

        return {
            key: DatetimeUnit.MONTH,
            unit: DatetimeUnit.MONTH,
            format: formatted[key],
            maxLength,
        };
    } else if (formatted[key] === 'YY' || formatted[key] === 'YYYY') {
        return {
            key: DatetimeUnit.YEAR,
            unit: DatetimeUnit.YEAR,
            format: formatted[key],
            maxLength: 4,
        };
    }
}
